<template>
  <div :class="isTouchDevice ? 'is-touch' : ''">
    <!-- スマホは PC と同じ見た目で縮めて出すので、題名は出さずタブに幅を使う -->
    <PageHeader :title="isMobile ? '' : 'クオリティレポート 予定・結果'">
      <template #title-header-content>
        <!-- タブ（RLS の稼働と同じ見た目）。押すたびに検索し直す。PC では画面の左右中央（.tabs） -->
        <div class="tabs bg-white flex text-white font-bold text-sm p-2 rounded ml-6 w-fit">
          <div class="w-28 rounded py-1 text-center cursor-pointer mr-2" :class="tab === 'progress' ? 'bg-blue-700' : 'bg-gray-400'" @click="changeTab('progress')">進捗</div>
          <div class="w-28 rounded py-1 text-center cursor-pointer" :class="tab === 'staff' ? 'bg-blue-700' : 'bg-gray-400'" @click="changeTab('staff')">スタッフごと</div>
        </div>

        <div class="flex-1 flex items-center justify-end mr-2">
          <!-- 進捗の地図（ピン＝今出ている物件。押すと予定日・担当者を入れられる。近い物件を同じ日に組む用） -->
          <button v-if="tab === 'progress'" type="button" class="map-btn mr-3" :class="isMap ? 'map-btn-active' : ''" @click="toggleMap()">
            <Icon iconName="LocationMarker" class="w-5 h-5 mr-1" />
            {{ isMap ? '地図を閉じる' : '地図' }}
          </button>
          <PrimaryButton text="検索" class="search-btn w-32 justify-center" @click="search()" />
          <div class="ml-4 text-lg font-bold text-white whitespace-nowrap">{{ countLabel }}</div>
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': loading }"
            class="refresh-icon ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="search()"
          />
        </div>
      </template>

      <template #page-header-content>
        <!-- PC はタブの真下（画面の左右中央）に月とチェック、右に検索欄（.filters の grid）。狭い画面は流れのまま -->
        <div class="filters">
          <div class="filters-center flex items-center flex-wrap gap-2">
            <!-- 月（進捗・スタッフごと共通。両方のタブの軸なので先頭） -->
            <div class="flex items-center text-white font-bold text-lg mx-2">
              <Icon iconName="ChevronLeft" :clickable="false" class="w-8 h-8 cursor-pointer hover:text-yellow-400" @click.native="moveMonth(-1)" />
              <span class="mx-2 whitespace-nowrap">{{ monthLabel }}</span>
              <Icon iconName="ChevronRight" :clickable="false" class="w-8 h-8 cursor-pointer hover:text-yellow-400" @click.native="moveMonth(1)" />
            </div>

            <!-- 進捗：その月の中で未完了・完了のどちらを出すか（RLS と同じ） -->
            <div v-if="tab === 'progress'" class="checks flex items-center whitespace-nowrap text-sm font-bold text-white">
              <div class="mx-2 flex items-center">
                <input id="notDone" v-model="filter.notDone" type="checkbox" class="w-5 h-5 rounded" @change="search()" />
                <label for="notDone" class="p-1 cursor-pointer">未完了 物件</label>
              </div>
              <div class="mx-2 flex items-center">
                <input id="done" v-model="filter.done" type="checkbox" class="w-5 h-5 rounded" @change="search()" />
                <label for="done" class="p-1 cursor-pointer">完了 物件</label>
              </div>
            </div>
          </div>

          <div class="filters-right flex items-center justify-end gap-2 flex-wrap">
            <template v-if="tab === 'progress'">
              <!-- 何か入っている欄は黄色（絞り込みに気づかず「出ない」とならないように） -->
              <input v-model="filter.siteName" type="search" placeholder="物件名 検索" class="filter-input w-56 rounded border border-gray-300 px-2 text-sm" :class="filter.siteName.trim() ? 'has-value' : ''" @keydown.enter="search()" />
              <!-- 担当者は自由入力（辞めた人も探せるよう候補に縛らない。「未定」で担当者が空の行） -->
              <input v-model="filter.staffName" type="search" placeholder="担当者 検索" class="filter-input w-40 rounded border border-gray-300 px-2 text-sm" :class="filter.staffName.trim() ? 'has-value' : ''" title="「未定」で担当者が空の行" @keydown.enter="search()" />
              <select v-model="filter.status" class="filter-input rounded border-gray-300 text-sm" :class="filter.status ? 'has-value' : ''" title="状態" @change="search()">
                <option value="">状態：全部</option>
                <option value="none">未予定</option>
                <option value="plan">予定済</option>
                <option value="doing">実施中</option>
                <option value="done">完了</option>
              </select>
            </template>
          </div>
        </div>
      </template>
    </PageHeader>

    <main class="absolute top-32 -mt-1 bottom-4 w-screen">
      <div class="h-full px-4 flex flex-col">
        <div class="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200 bg-white">
          <!-- 地図（進捗で MAP を押したとき。表の上に出す。緯度経度が取れない物件はピン無し） -->
          <div v-if="tab === 'progress' && isMap" class="qr-map-wrap">
            <div ref="map" class="qr-map"></div>
            <div class="qr-map-legend">
              <span><i class="dot" style="background:#6b7280"></i>未予定</span>
              <span><i class="dot" style="background:#2563eb"></i>予定済（日付）</span>
              <span><i class="dot" style="background:#d97706"></i>実施中</span>
              <span><i class="dot" style="background:#16a34a"></i>完了</span>
              <span><i class="dot dot-me"></i>現在地</span>
              <span class="text-gray-500">ピンを押すと予定日・担当者を入れられます</span>
              <span v-if="noPinCount" class="text-red-600">住所から位置が取れない物件 {{ noPinCount }} 件</span>
            </div>
          </div>

          <div v-if="loading" class="flex h-full items-center justify-center font-bold text-gray-400">
            Searching...
          </div>

          <!-- 進捗でどちらのチェックも OFF -->
          <div v-else-if="tab === 'progress' && !filter.notDone && !filter.done" class="flex h-full items-center justify-center font-bold text-red-700">
            「未完了 物件」か「完了 物件」かどちらかONにしてください。
          </div>

          <div v-else-if="tab === 'progress' && !list.length" class="flex h-full items-center justify-center font-bold text-gray-400">
            対象データがありません。
          </div>

          <!-- ========== 進捗（PC） ========== -->
          <table v-else-if="tab === 'progress'" class="w-full border-collapse text-sm list-table">
            <thead class="sticky top-0 bg-gray-100 z-10">
              <tr class="border-b border-gray-300 text-left text-xs text-gray-600">
                <th class="px-3 py-2">物件</th>
                <th class="px-3 py-2 text-center">マスタ</th>
                <th class="px-3 py-2">前回完了日</th>
                <th class="px-3 py-2">予定日</th>
                <th class="px-3 py-2">担当者</th>
                <th class="px-3 py-2 text-center">完了</th>
                <th class="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody class="text-gray-700">
              <!-- 完了した行は行ごと押して報告書を開く（リンク・ボタンは別） -->
              <tr v-for="row in pagedList" :key="row.id" class="border-b border-gray-200" :class="isDone(row) ? 'bg-gray-50 done-row' : ''" @click="isDone(row) && openForm(row)">
                <td class="px-3 py-2">
                  <div class="font-bold">{{ row.siteName }}</div>
                  <div class="text-xs text-gray-500">
                    {{ row.siteAddress }}
                    <!-- 地図を出しているとき：この物件のピンへ寄せて光らせる -->
                    <a v-if="isMap && row.latitude && row.longitude" class="master-link pin-link" @click.stop="focusPin(row)"><Icon iconName="LocationMarker" :clickable="false" class="w-4 h-4" />地図で見る</a>
                  </div>
                  <div class="text-xs text-gray-500">{{ clientName(row) }}</div>
                </td>
                <!-- マスタ（撮影設定）：別タブで開くリンクと、合計写真枚数（未設定は赤） -->
                <td class="px-3 py-2 whitespace-nowrap text-center">
                  <a class="master-link master-link-col" @click.stop="openMaster(row)">マスタを開く</a>
                  <div v-if="row.totalPhoto" class="text-xs text-gray-500 mt-1">{{ row.totalPhoto }} 枚</div>
                  <div v-else class="mt-1"><span class="chip chip-alert">未設定</span></div>
                </td>
                <!-- 前回完了（この月より前で一番新しい完了日。予定の間隔の目安） -->
                <td class="px-3 py-2 whitespace-nowrap text-gray-500">{{ fmtDate(row.prevCompletionDate) }}</td>
                <!-- 予定日（未実施は変えたら即保存。実施中・完了は入力部品を出さず文字だけ）。マスタの実施日と違う日なら振替マーク -->
                <td class="px-3 py-2 whitespace-nowrap">
                  <template v-if="canEdit(row)">
                    <div class="flex items-center gap-2">
                      <WmsDateField :name="'date' + row.id" :value="row.scheduledDate" :min="today" class="w-36" @change="changeDate(row, $event)" />
                      <span v-if="row.shifted" class="chip chip-shift" :title="'マスタの実施日 ' + fmtDate(row.masterDate)">振替</span>
                    </div>
                    <div v-if="row.shifted" class="text-xs text-gray-500 mt-0.5">マスタの実施日 {{ fmtDate(row.masterDate) }}</div>
                  </template>
                  <template v-else>
                    <span>{{ fmtDate(row.scheduledDate) }}</span>
                    <span v-if="!isDone(row) && row.shifted" class="chip chip-shift ml-2" :title="'マスタの実施日 ' + fmtDate(row.masterDate)">振替</span>
                  </template>
                </td>
                <!-- 担当者（未実施は変えたら即保存。候補は予定日に在職中の作業員。実施中・完了は文字だけ） -->
                <td class="px-3 py-2">
                  <WmsSearchSelect
                    v-if="canEdit(row)"
                    :name="'staff' + row.id"
                    :value="staffOf(row)"
                    :options="staffOptions(row)"
                    track-by="userId"
                    label="name"
                    placeholder=""
                    class="w-48"
                    @input="changeStaff(row, $event)"
                  />
                  <span v-else class="whitespace-nowrap">{{ row.staffName || '―' }}</span>
                </td>
                <!-- 完了：完了日・時間帯（実施中は開始時間だけ） -->
                <td class="px-3 py-2 whitespace-nowrap text-center">
                  <template v-if="isDone(row)">
                    <span class="font-bold">{{ fmtDate(row.completionDate) }}</span>
                    <span class="text-xs text-gray-500 ml-1">{{ row.startTime }}〜{{ row.endTime || '' }}</span>
                  </template>
                  <span v-else-if="row.startTime" class="text-xs text-gray-500">{{ row.startTime }}〜</span>
                  <span v-else>―</span>
                </td>
                <td class="px-3 py-2 whitespace-nowrap text-center">
                  <!-- 未完了：報告書画面へ（実施中はオレンジの「実施中」。予定日が無ければ実施できない）。マスタの合計撮影枚数が無ければボタンを出さず赤字（終了できないため） -->
                  <template v-if="!isDone(row)">
                    <div v-if="needMaster(row)" class="text-xs font-bold text-red-600 whitespace-normal">マスタで「合計撮影枚数」を要設定</div>
                    <template v-else>
                      <PrimaryButton :text="startText(row)" size="sm" class="start-btn w-24" :class="startClass(row)" :disabled="!canStart(row)" @click="openForm(row)" />
                      <div v-if="!canStart(row)" class="text-xs text-red-600 mt-0.5">予定日を入れてください</div>
                    </template>
                  </template>
                  <!-- 完了：報告書は行を押して開く。PDF はダウンロードのアイコン（定期清掃の一覧と同じ） -->
                  <span v-else @click.stop>
                    <PrimaryButton size="sm" class="pdf-btn" :buttonContents="'要'" :disabled="making" title="PDF" @click="openPdf(row)">
                      <Icon slot="before" iconName="Download" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
                    </PrimaryButton>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- ========== 進捗（スマホ：カード） ========== -->
          <div v-if="tab === 'progress' && !loading && list.length" class="cards">
            <!-- 完了したカードはカードごと押して報告書を開く（リンク・ボタンは別） -->
            <div v-for="row in pagedList" :key="'c' + row.id" class="card" :class="isDone(row) ? 'done-row' : ''" @click="isDone(row) && openForm(row)">
              <div class="flex items-start gap-2">
                <div class="min-w-0 flex-1">
                  <div class="font-bold text-base leading-tight">{{ row.siteName }}</div>
                  <div class="text-xs text-gray-500">
                    {{ row.siteAddress }}<a class="master-link" @click.stop="openMaster(row)">マスタを開く</a>
                    <a v-if="isMap && row.latitude && row.longitude" class="master-link pin-link" @click.stop="focusPin(row)"><Icon iconName="LocationMarker" :clickable="false" class="w-4 h-4" />地図で見る</a>
                  </div>
                  <div class="text-xs text-gray-500">{{ clientName(row) }}</div>
                </div>
                <!-- 状態のチップは完了だけ（未完了は予定日と「実施中」ボタンで分かる） -->
                <span v-if="isDone(row)" class="chip" :class="statusOf(row).cls">{{ statusOf(row).text }}</span>
              </div>
              <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 mt-1">
                <span class="text-gray-500">前回完了日 {{ fmtDate(row.prevCompletionDate) }}</span>
                <span>予定日 <b>{{ fmtDate(row.scheduledDate) }}</b><span v-if="row.shifted" class="chip chip-shift ml-1">振替</span></span>
                <span v-if="isDone(row)">完了日 <b>{{ fmtDate(row.completionDate) }}</b></span>
                <span>担当 <b>{{ row.staffName || '未定' }}</b></span>
                <span v-if="row.startTime">{{ row.startTime }}〜{{ row.endTime || '' }}</span>
                <span v-if="!isDone(row) && !row.totalPhoto" class="chip chip-alert">マスタ未設定</span>
              </div>
              <!-- ボタンは押しやすい高さ（44px）。完了は PDF だけ（報告書はカードを押す） -->
              <div class="card-btns mt-2" @click.stop>
                <template v-if="!isDone(row)">
                  <div v-if="needMaster(row)" class="text-sm font-bold text-red-600 self-center">マスタで「合計撮影枚数」を要設定</div>
                  <template v-else>
                    <div v-if="!canStart(row)" class="text-xs text-red-600 self-center">予定日を入れてください</div>
                    <PrimaryButton :text="startText(row)" size="lg" class="start-btn card-btn" :class="startClass(row)" :disabled="!canStart(row)" @click="openForm(row)" />
                  </template>
                </template>
                <template v-else>
                  <PrimaryButton class="card-btn pdf-btn" :buttonContents="'要'" :disabled="making" title="PDF" @click="openPdf(row)">
                    <Icon slot="before" iconName="Download" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
                  </PrimaryButton>
                </template>
              </div>
            </div>
          </div>

          <!-- ========== スタッフごと（1 行＝担当者。押すと担当の物件を下に展開） ========== -->
          <div v-else-if="tab === 'staff' && !summary.length" class="flex h-full items-center justify-center font-bold text-gray-400">
            対象データがありません。
          </div>

          <table v-else-if="tab === 'staff'" class="w-full border-collapse text-sm staff-table">
            <thead class="sticky top-0 bg-gray-100 z-10">
              <tr class="border-b border-gray-300 text-xs text-gray-600">
                <th class="px-3 py-2 text-left">担当者</th>
                <th class="px-3 py-2 text-center">担当件数</th>
                <th class="px-3 py-2 text-center">未予定</th>
                <th class="px-3 py-2 text-center">予定済</th>
                <th class="px-3 py-2 text-center">実施中</th>
                <th class="px-3 py-2 text-center">完了</th>
                <th class="px-3 py-2 text-left">進み</th>
              </tr>
            </thead>
            <tbody class="text-gray-700">
              <template v-for="s in summary">
                <tr
                  :key="staffKey(s)"
                  class="border-b border-gray-200 cursor-pointer hover:bg-green-50"
                  :class="s.staffId === null ? 'bg-yellow-50' : ''"
                  @click="toggleStaff(s)"
                >
                  <td class="px-3 py-2 font-bold whitespace-nowrap">
                    <span class="inline-block w-4 text-gray-400">{{ expanded[staffKey(s)] ? '▼' : '▶' }}</span>
                    <span :class="s.staffId === null ? 'text-red-600' : ''">{{ s.staffName || '未定' }}</span>
                  </td>
                  <td class="px-3 py-2 text-center font-bold text-base">{{ s.total }}</td>
                  <td class="px-3 py-2 text-center">{{ s.noneCount }}</td>
                  <td class="px-3 py-2 text-center">{{ s.planCount }}</td>
                  <td class="px-3 py-2 text-center">{{ s.doingCount }}</td>
                  <td class="px-3 py-2 text-center">{{ s.doneCount }}</td>
                  <!-- 完了の割合 -->
                  <td class="px-3 py-2 w-56">
                    <div class="flex items-center gap-2">
                      <div class="bar"><div class="bar-in" :style="{ width: progressRate(s) + '%' }"></div></div>
                      <span class="text-xs font-bold w-10 text-right">{{ progressRate(s) }}%</span>
                    </div>
                  </td>
                </tr>

                <!-- 展開：その担当者の物件 -->
                <tr v-if="expanded[staffKey(s)]" :key="staffKey(s) + 'x'" class="border-b border-gray-300">
                  <td colspan="7" class="p-0 bg-gray-50">
                    <div v-if="rowsLoading[staffKey(s)]" class="p-3 text-center font-bold text-gray-400">Loading...</div>
                    <div v-else-if="!staffRows[staffKey(s)].length" class="p-3 text-center font-bold text-gray-400">対象データがありません。</div>
                    <table v-else class="w-full border-collapse text-sm sub-table">
                      <tbody>
                        <!-- 完了した行は押して報告書を開く（実施は進捗タブから） -->
                        <tr v-for="row in staffRows[staffKey(s)]" :key="'s' + row.id" class="border-t border-gray-200" :class="isDone(row) ? 'done-row' : ''" @click="isDone(row) && openForm(row)">
                          <td class="pl-10 pr-3 py-2">
                            <div class="font-bold">{{ row.siteName }}</div>
                            <div class="text-xs text-gray-500">{{ row.siteAddress }}</div>
                          </td>
                          <td class="px-3 py-2 whitespace-nowrap">
                            予定日 <b>{{ fmtDate(row.scheduledDate) }}</b><span v-if="row.shifted" class="chip chip-shift ml-1">振替</span>
                          </td>
                          <td class="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                            <span v-if="row.startTime">{{ row.startTime }}〜{{ row.endTime || '' }}</span>
                            <span v-else>―</span>
                          </td>
                          <td class="px-3 py-2 text-center whitespace-nowrap">
                            <span class="chip" :class="statusOf(row).cls">{{ statusOf(row).text }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- 20 件ごと（進捗だけ） -->
        <div v-if="tab === 'progress' && total > perPage" class="my-2 w-fit mx-auto">
          <pagination id="pagenation" v-model="page" :per-page="perPage" :records="total" class="text-center" />
        </div>
      </div>
    </main>


    <!-- 地図のピンを押したとき：その物件の予定日・担当者（未完了だけ変えられる） -->
    <div v-if="pinRow" class="pin-dialog-bg" @click.self="pinRow = null">
      <div class="pin-dialog">
        <div class="font-bold text-base">{{ pinRow.siteName }}</div>
        <div class="text-xs text-gray-500">{{ pinRow.siteAddress }}</div>
        <div class="text-sm text-gray-500 mb-3">前回完了日 {{ fmtDate(pinRow.prevCompletionDate) }}</div>
        <template v-if="Number(pinRow.status) < 2">
          <div class="text-sm font-bold text-gray-600 mb-1">予定日</div>
          <WmsDateField name="pinDate" :value="pinDate" :min="today" class="w-40 mb-3" @change="pinDate = $event" />
          <div class="text-sm font-bold text-gray-600 mb-1">担当者</div>
          <WmsSearchSelect
            name="pinStaff"
            :value="pinStaff"
            :options="staffOptions(pinRow)"
            track-by="userId"
            label="name"
            placeholder=""
            class="w-64 mb-4"
            @input="pinStaff = $event"
          />
          <div class="flex justify-end gap-2">
            <WhiteButton text="閉じる" @click="pinRow = null" />
            <PrimaryButton text="保存" class="w-28" :disabled="pinSaving" @click="savePin()" />
          </div>
        </template>
        <template v-else>
          <div class="flex items-center gap-2 text-sm">
            <span class="chip" :class="statusOf(pinRow).cls">{{ statusOf(pinRow).text }}</span>
            <span>予定日 <b>{{ fmtDate(pinRow.scheduledDate) }}</b></span>
            <span>担当 <b>{{ pinRow.staffName || '未定' }}</b></span>
          </div>
          <div class="flex justify-end mt-4">
            <WhiteButton text="閉じる" @click="pinRow = null" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PageHeader from '@components/PageHeader.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import WhiteButton from '@components/WhiteButton.vue'
import WmsDateField from '@wmscomponents/WmsDateField.vue'
import WmsSearchSelect from '@wmscomponents/WmsSearchSelect.vue'
import Pagination from 'vue-pagination-2'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import * as logManager from '@managers/logManager'
import * as utils from '@libs/utils'
import moment from 'moment'
import PageModel from '../../main/pages/PageModel.vue'

const DOW_NAME = ['日', '月', '火', '水', '木', '金', '土']
// 検索の条件を控えるセッションストレージのキー
const SESSION_STORAGE_NAME = 'qualityReportSchedule'

// 地図（google.maps は public/index.html で読み込み済み）。リアクティブにすると重いので data に入れない
let map = null
let markers = []
// 現在地の青い丸
let myMarker = null
// 「地図で見る」で光らせているピン（元に戻すタイマーと行）
let focusTimer = null
let focusRow = null
// ピンの色（未予定・予定済・実施中・完了）
const PIN_COLOR = { none: '#6b7280', plan: '#2563eb', doing: '#d97706', done: '#16a34a' }

export default {
  extends: PageModel,
  components: {
    Icon,
    PageHeader,
    PrimaryButton,
    WhiteButton,
    WmsDateField,
    WmsSearchSelect,
    Pagination
  },

  data() {
    return {
      // 進捗（progress）／スタッフごと（staff）
      tab: 'progress',
      loading: false,
      // 表示中の月
      month: moment().format('YYYY-MM'),
      // 絞り込み（進捗）。未完了は最初から ON、完了は OFF（RLS と同じ）
      filter: { notDone: true, done: false, siteName: '', staffId: '', staffName: '', status: '' },
      // 進捗の一覧
      list: [],
      // 担当者の候補（退職者も含む。在職中かは行の予定日と比べる）
      staffList: [],
      // ページ（地図が無いときは SQL の LIMIT で 1 ページ分だけ取る。total＝絞り込み後の全件数）
      page: 1,
      perPage: 20,
      total: 0,
      // スタッフごと：担当者ごとの件数。展開した担当者の物件は staffRows（キーは staffKey）
      summary: [],
      expanded: {},
      staffRows: {},
      rowsLoading: {},
      // PDF を作っている間
      making: false,
      // 地図（進捗）。noPinCount＝住所から位置が取れずピンにできなかった物件の数
      isMap: false,
      noPinCount: 0,
      // 現在地（地図を開くたびに取り直す）
      myPos: null,
      // ピンを押したときのダイアログ
      pinRow: null,
      pinDate: '',
      pinStaff: null,
      pinSaving: false
    }
  },

  computed: {
    // スマホか（ルーターで PC と同じ見た目にしているので、幅ではなく端末で判定）
    isMobile() {
      return utils.deviceInfo.isMobile
    },

    // スマホかタブレットか（PC と同じ見た目でも幅は 980px 相当なので、ヘッダーと一覧の置き方を変える）
    isTouchDevice() {
      return utils.deviceInfo.isMobile || utils.deviceInfo.isTablet
    },

    monthLabel() {
      return moment(this.month + '-01').format('YYYY年M月')
    },

    // 予定日は今日以降だけ入れられる
    today() {
      return moment().format('YYYY-MM-DD')
    },

    // ヘッダーの件数（進捗は物件の数、スタッフごとは担当者の数）
    countLabel() {
      return this.tab === 'progress' ? `${this.total} 件` : `${this.summary.length} 人`
    },

    // 表に出す行。地図のときは全行を持っているので画面で分け、そうでなければ list が 1 ページ分
    pagedList() {
      if (!this.isMap) {
        return this.list
      }
      const start = (this.page - 1) * this.perPage
      return this.list.slice(start, start + this.perPage)
    }
  },

  async created() {
    // 報告書を見て戻ったときに検索の条件（月・タブ・未完了／完了のチェック・検索欄）が元に戻らないよう、セッションストレージから復元する
    this.restoreSession()
    await this.getStaffList()
    await this.search()
  },

  watch: {
    // ページ送り（地図が無いときは 1 ページ分だけ取っているので取り直す。地図のときは画面で分けるだけ）
    page() {
      if (!this.isMap) {
        this.fetchList()
      }
    }
  },

  beforeDestroy() {
    this.clearMarkers()
    map = null
  },

  methods: {
    /**
     * 担当者の候補
     * API: qualityReport/staff
     */
    async getStaffList() {
      try {
        const res = await backend.searchData('qualityReport/staff', {})
        this.staffList = (res.data && res.data.data) || []
      } catch (e) {
        console.log(e)
        this.staffList = []
      }
    },

    /**
     * タブ：押すたびに検索し直す
     */
    async changeTab(tab) {
      this.tab = tab
      await this.search()
    },

    /**
     * 月を動かす
     */
    async moveMonth(n) {
      this.month = moment(this.month + '-01').add(n, 'months').format('YYYY-MM')
      await this.search()
    },

    /**
     * 検索（タブごと）。条件はセッションストレージに控える
     */
    async search() {
      this.saveSession()
      if (this.tab === 'progress') {
        await this.searchList()
      } else {
        await this.searchSummary()
      }
    },

    /**
     * 検索の条件をセッションストレージに控える（月・タブ・未完了／完了のチェック・検索欄。地図の表示は控えない）
     */
    saveSession() {
      try {
        sessionStorage.setItem(SESSION_STORAGE_NAME, JSON.stringify({ month: this.month, tab: this.tab, filter: this.filter }))
      } catch (e) {
        console.log(e)
      }
    },

    /**
     * セッションストレージの条件を戻す（無い・壊れていれば初期値のまま）
     */
    restoreSession() {
      try {
        const s = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_NAME))
        if (!s) {
          return
        }
        if (s.month) {
          this.month = s.month
        }
        if (s.tab) {
          this.tab = s.tab
        }
        if (s.filter) {
          this.filter = { ...this.filter, ...s.filter }
        }
      } catch (e) {
        console.log(e)
      }
    },

    /**
     * 進捗の一覧
     * API: qualityReport/list
     */
    async searchList() {
      // 1 ページ目に戻す。地図が無いときはページの watch が取りに行くので、ここでは戻すだけ
      if (this.page !== 1 && !this.isMap) {
        this.page = 1
        return
      }
      this.page = 1
      await this.fetchList()
    },

    /**
     * 今のページを取る（地図のときは全行。そうでなければ SQL の LIMIT で 1 ページ分）
     * API: qualityReport/list
     */
    async fetchList() {
      if (!this.filter.notDone && !this.filter.done) {
        this.list = []
        this.total = 0
        return
      }
      this.loading = true
      try {
        const res = await backend.searchData('qualityReport/list', {
          month: this.month,
          notDone: this.filter.notDone ? 1 : 0,
          done: this.filter.done ? 1 : 0,
          siteName: this.filter.siteName.trim(),
          staffId: this.filter.staffId,
          staffName: this.filter.staffName.trim(),
          status: this.filter.status,
          isMap: this.isMap ? 1 : 0,
          page: this.isMap ? '' : this.page,
          perPage: this.perPage,
          userId: this.$store.getters.user.id
        })
        this.list = (res.data && res.data.data) || []
        this.total = this.list.length ? Number(this.list[0].totalCount) : 0
      } catch (e) {
        console.log(e)
        this.list = []
        this.total = 0
        await dialogs.showErrorDialog('エラー', '一覧の取得に失敗しました。')
      }
      this.loading = false
      if (this.isMap) {
        await this.$nextTick()
        await this.initMap(true)
      }
    },

    /**
     * 地図の表示／非表示（出すときは緯度経度付きで検索し直す）
     */
    async toggleMap() {
      this.isMap = !this.isMap
      if (!this.isMap) {
        this.clearMarkers()
        map = null
      }
      // 開くたびに現在地を取り直す（移動してから開き直しても前の場所のままにならないように）
      this.myPos = null
      // 地図あり＝全行、なし＝1 ページ分なので、切り替えたら取り直す
      await this.searchList()
    },

    /**
     * 地図を描く（ピン＝今出ている物件、青い丸＝現在地。keep＝今の拡大と中心を保つ）
     * 最初は現在地を中心に近所だけ（zoom 14。遠い物件のピンは画面の外。予定を組むときは近い物件だけ見えればよい）。
     * 現在地が取れなければピンの真ん中を広めに
     */
    async initMap(keep) {
      if (!this.$refs.map || typeof google === 'undefined') {
        return
      }
      const rows = this.list.filter((r) => r.latitude && r.longitude)
      this.noPinCount = this.list.length - rows.length
      if (!this.myPos) {
        this.myPos = await this.getMyPos()
      }
      // 地図が消えていたら（取っている間に地図を閉じた）何もしない
      if (!this.$refs.map) {
        return
      }
      let zoom = 12
      let center = { lat: 35.624315, lng: 139.579387 }
      if (this.myPos) {
        zoom = 14
        center = this.myPos
      } else if (rows.length) {
        center = {
          lat: rows.reduce((s, r) => s + Number(r.latitude), 0) / rows.length,
          lng: rows.reduce((s, r) => s + Number(r.longitude), 0) / rows.length
        }
      }
      if (keep && map) {
        zoom = map.getZoom()
        center = map.getCenter()
      }
      this.clearMarkers()
      map = new google.maps.Map(this.$refs.map, {
        zoom,
        center,
        // 店や学校のラベルは消す（物件のピンを見やすく）
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }]
      })
      markers = rows.map((row) => {
        const marker = new google.maps.Marker({
          map,
          position: { lat: Number(row.latitude), lng: Number(row.longitude) },
          title: row.siteName,
          icon: this.pinIcon(row),
          optimized: false,
          zIndex: this.isDone(row) ? 1 : 2
        })
        marker.rowId = row.id
        marker.addListener('click', () => this.openPin(row))
        return marker
      })
      if (this.myPos) {
        myMarker = new google.maps.Marker({
          map,
          position: this.myPos,
          title: '現在地',
          icon: this.myIcon(),
          optimized: false,
          zIndex: 3
        })
      }
    },

    /**
     * 現在地（取れなければ null。PC は Wi-Fi などからの推定なのでずれることがある）
     */
    async getMyPos() {
      try {
        const pos = await utils.getLocation2()
        if (pos && pos.lat && pos.lng) {
          return { lat: pos.lat, lng: pos.lng }
        }
      } catch (e) {
        console.log(e)
      }
      return null
    },

    /**
     * 現在地の絵（青い丸。実施画面と同じ）
     */
    myIcon() {
      // 大きめの青い丸＋「現在地」の札（物件の吹き出しに紛れないように）
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="72" height="78" viewBox="0 0 72 78">
        <circle cx="36" cy="36" r="34" fill="#3b82f6" fill-opacity="0.22"/>
        <circle cx="36" cy="36" r="20" fill="#3b82f6" fill-opacity="0.35"/>
        <circle cx="36" cy="36" r="11" fill="#2563eb" stroke="#fff" stroke-width="3"/>
        <rect x="14" y="58" width="44" height="18" rx="9" fill="#2563eb"/>
        <text x="36" y="71" text-anchor="middle" font-size="12" font-weight="bold" font-family="sans-serif" fill="#fff">現在地</text>
      </svg>`
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new google.maps.Size(72, 78),
        anchor: new google.maps.Point(36, 36)
      }
    },

    /**
     * ピンを消す（現在地も）
     */
    clearMarkers() {
      this.unfocusPin()
      markers.forEach((m) => m.setMap(null))
      markers = []
      if (myMarker) {
        myMarker.setMap(null)
        myMarker = null
      }
    },

    /**
     * 表の「地図で見る」：地図をその物件へ寄せて、ピンを黄色にして数秒はねさせる（表のどの行が地図のどこか分かるように）
     */
    focusPin(row) {
      const marker = markers.find((m) => m.rowId === row.id)
      if (!map || !marker) {
        return
      }
      this.unfocusPin()
      // 地図が画面の外にあれば見える所まで戻す
      if (this.$refs.map && this.$refs.map.scrollIntoView) {
        this.$refs.map.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      map.panTo(marker.getPosition())
      if (map.getZoom() < 15) {
        map.setZoom(15)
      }
      focusRow = row
      marker.setIcon(this.pinIcon(row, true))
      marker.setZIndex(10)
      marker.setAnimation(google.maps.Animation.BOUNCE)
      focusTimer = setTimeout(() => this.unfocusPin(), 4000)
    },

    /**
     * 光らせているピンを元に戻す
     */
    unfocusPin() {
      if (focusTimer) {
        clearTimeout(focusTimer)
        focusTimer = null
      }
      if (focusRow) {
        const marker = markers.find((m) => m.rowId === focusRow.id)
        if (marker) {
          marker.setAnimation(null)
          marker.setIcon(this.pinIcon(focusRow))
          marker.setZIndex(this.isDone(focusRow) ? 1 : 2)
        }
        focusRow = null
      }
    },

    /**
     * ピンの絵（色は状態、文字は予定日 M/D。focus＝「地図で見る」で光らせる：黄色の地・太い枠）
     */
    pinIcon(row, focus) {
      const st = Number(row.status)
      const color = st >= 3 ? PIN_COLOR.done : st === 2 ? PIN_COLOR.doing : row.scheduledDate ? PIN_COLOR.plan : PIN_COLOR.none
      const text = row.scheduledDate ? moment(row.scheduledDate).format('M/D') : '未定'
      const fill = focus ? '#fde047' : '#fff'
      const stroke = focus ? 5 : 3
      // 日付は吹き出しの中に大きく（丸の中だと地図の文字と重なって読めない）。下の点が物件の位置
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="68" height="62" viewBox="0 0 68 62">
        <rect x="3" y="3" width="62" height="32" rx="9" fill="${fill}" stroke="${color}" stroke-width="${stroke}"/>
        <path d="M25 35 L43 35 L34 48 Z" fill="${color}"/>
        <circle cx="34" cy="54" r="5" fill="${color}" stroke="#fff" stroke-width="2"/>
        <text x="34" y="25" text-anchor="middle" font-size="16" font-weight="bold" font-family="sans-serif" fill="${color}">${text}</text>
      </svg>`
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new google.maps.Size(68, 62),
        anchor: new google.maps.Point(34, 54)
      }
    },

    /**
     * ピンを押した → 予定日・担当者のダイアログ
     */
    openPin(row) {
      this.pinRow = row
      this.pinDate = row.scheduledDate || ''
      this.pinStaff = this.staffOf(row)
    },

    /**
     * ダイアログの保存（行の即保存と同じ）→ ピンの色・日付を描き直す
     */
    async savePin() {
      const row = this.pinRow
      if (this.pinDate && this.pinDate < this.today) {
        await dialogs.showErrorDialog('予定日', '予定日は今日以降の日付にしてください。')
        return
      }
      this.pinSaving = true
      row.scheduledDate = this.pinDate || null
      row.staffId = this.pinStaff ? Number(this.pinStaff.userId) : null
      row.staffName = this.pinStaff ? this.pinStaff.name : null
      await this.saveRow(row)
      const marker = markers.find((m) => m.rowId === row.id)
      if (marker) {
        marker.setIcon(this.pinIcon(row))
      }
      this.pinSaving = false
      this.pinRow = null
    },

    /**
     * スタッフごと：担当者ごとの件数（展開は閉じ直す）
     * API: qualityReport/staffSummary
     */
    async searchSummary() {
      this.loading = true
      try {
        const res = await backend.searchData('qualityReport/staffSummary', {
          month: this.month,
          userId: this.$store.getters.user.id
        })
        this.summary = (res.data && res.data.data) || []
        // 展開の状態はキーを最初からそろえておく（後から足すと画面が追いつかない）
        const expanded = {}
        const rows = {}
        const loading = {}
        this.summary.forEach((s) => {
          const key = this.staffKey(s)
          expanded[key] = false
          rows[key] = []
          loading[key] = false
        })
        this.expanded = expanded
        this.staffRows = rows
        this.rowsLoading = loading
      } catch (e) {
        console.log(e)
        this.summary = []
        await dialogs.showErrorDialog('エラー', '一覧の取得に失敗しました。')
      }
      this.loading = false
    },

    /**
     * スタッフごとの行のキー（未定は 'none'）
     */
    staffKey(s) {
      return s.staffId === null || s.staffId === undefined ? 'none' : String(s.staffId)
    },

    /**
     * 完了の割合
     */
    progressRate(s) {
      const total = Number(s.total) || 0
      if (!total) {
        return 0
      }
      return Math.round((Number(s.doneCount) || 0) * 100 / total)
    },

    /**
     * 担当者の行を押す → その人の物件を下に展開（もう一度押すと閉じる）
     * API: qualityReport/list
     */
    async toggleStaff(s) {
      const key = this.staffKey(s)
      if (this.expanded[key]) {
        this.expanded[key] = false
        return
      }
      this.expanded[key] = true
      if (this.staffRows[key].length) {
        return
      }
      this.rowsLoading[key] = true
      try {
        const res = await backend.searchData('qualityReport/list', {
          month: this.month,
          notDone: 1,
          done: 1,
          siteName: '',
          staffId: key,
          status: '',
          userId: this.$store.getters.user.id
        })
        this.staffRows[key] = (res.data && res.data.data) || []
      } catch (e) {
        console.log(e)
        this.staffRows[key] = []
        await dialogs.showErrorDialog('エラー', '一覧の取得に失敗しました。')
      }
      this.rowsLoading[key] = false
    },

    /**
     * 日付の表示（M/D（曜））
     */
    fmtDate(d) {
      if (!d) {
        return '―'
      }
      const m = moment(d)
      return `${m.format('M/D')}（${DOW_NAME[m.day()]}）`
    },

    /**
     * 完了しているか
     */
    isDone(row) {
      return Number(row.status) >= 3
    },

    /**
     * 状態チップ
     */
    statusOf(row) {
      const st = Number(row.status)
      if (st >= 3) {
        return { cls: 'chip-done', text: '完了' }
      }
      if (st === 2) {
        return { cls: 'chip-doing', text: '実施中' }
      }
      if (row.scheduledDate) {
        return { cls: 'chip-plan', text: '予定済' }
      }
      return { cls: 'chip-none', text: '未予定' }
    },

    /**
     * その行の担当者（候補のオブジェクト）
     */
    staffOf(row) {
      if (row.staffId === null || row.staffId === undefined) {
        return null
      }
      const s = this.staffList.find((x) => Number(x.userId) === Number(row.staffId))
      return s || { userId: Number(row.staffId), name: (row.staffName || '不明') + '（作業員の登録なし）' }
    },

    /**
     * その行で選べる担当者（予定日に在職中の作業員。今の担当者は必ず入れる）
     * 在職中でない＝最後の日（最終勤務日。無ければ退職日）が予定日より前（最終勤務日の翌日以降は選べない）
     */
    staffOptions(row) {
      const date = row.scheduledDate || moment().format('YYYY-MM-DD')
      return this.staffList.filter((s) => {
        if (Number(s.userId) === Number(row.staffId)) {
          return true
        }
        if (s.leaveDate && s.leaveDate < date) {
          return false
        }
        return true
      })
    },

    /**
     * 予定日を変えた → 即保存
     */
    async changeDate(row, value) {
      // 過去の日付は入れられない（カレンダーでも止めているが、手入力の保険）
      if (value && value < this.today) {
        await dialogs.showErrorDialog('予定日', '予定日は今日以降の日付にしてください。')
        // 欄の表示を元に戻す
        const before = row.scheduledDate
        row.scheduledDate = ''
        await this.$nextTick()
        row.scheduledDate = before
        return
      }
      row.scheduledDate = value || null
      await this.saveRow(row)
    },

    /**
     * 担当者を変えた → 即保存
     */
    async changeStaff(row, staff) {
      row.staffId = staff ? Number(staff.userId) : null
      row.staffName = staff ? staff.name : null
      await this.saveRow(row)
    },

    /**
     * 行の保存（予定日・担当者）
     * API: qualityReport/saveSchedule
     */
    async saveRow(row) {
      try {
        const res = await backend.postData('qualityReport/saveSchedule', {
          id: row.id,
          scheduledDate: row.scheduledDate,
          staffId: row.staffId,
          userId: this.$store.getters.user.id
        })
        if (res.data !== 'complete') {
          throw new Error(res.data)
        }
        // 振替マーク（マスタの実施日があって、予定日がそれと違う）
        row.shifted = !!(row.masterDate && row.scheduledDate && row.scheduledDate !== row.masterDate)
        logManager.recording(this, logManager.Loglevel.INFO, 'クオリティレポート', '予定', `物件名：${row.siteName}（${this.monthLabel}）の予定を 予定日：${row.scheduledDate || '未予定'}、担当者：${row.staffName || '未定'} にしました。`)
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('保存失敗', 'エラー発生\r\n保存に失敗しました。')
        await this.search()
      }
    },

    /**
     * 実施できるか（未完了は予定日が必要。実施中・完了は常に開ける）
     */
    canStart(row) {
      return Number(row.status) >= 2 || !!row.scheduledDate
    },

    /**
     * 予定日・担当者を変えられるか（未実施だけ。実施中・完了は文字で出す）
     */
    canEdit(row) {
      return Number(row.status) < 2
    },

    /**
     * 実施する前にマスタの設定が要るか（合計撮影枚数が無いと終了できず、実施中はマスタも変えられないので、実施前に止める）
     */
    needMaster(row) {
      return Number(row.status) < 2 && !row.totalPhoto
    },

    /**
     * 得意先名（1 と 2 をつなげる）
     */
    clientName(row) {
      return [row.clientName1, row.clientName2].filter((v) => v).join(' ')
    },

    /**
     * 「実施する」ボタンの文字（実施中は「実施中」）
     */
    startText(row) {
      return Number(row.status) === 2 ? '実施中' : '実施する'
    },

    /**
     * 「実施する」ボタンの色（実施中はオレンジ、それ以外は緑＝画面を移るボタン）
     */
    startClass(row) {
      // この Tailwind には orange が無いので yellow-600（#d97706＝実施中のピンと同じ色）
      return Number(row.status) === 2 ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'
    },

    /**
     * 報告書画面へ（未完了は実施、完了は見るだけ）
     */
    async openForm(row) {
      if (this.needMaster(row)) {
        await dialogs.showErrorDialog('実施', 'マスタで「合計撮影枚数」を設定してから実施してください。')
        return
      }
      if (!this.canStart(row)) {
        await dialogs.showErrorDialog('実施', '予定日を入れてから実施してください。')
        return
      }
      // 実施前で、前回の完了から 14 日経っていなければ確認（止めない）
      if (Number(row.status) < 2 && row.prevCompletionDate) {
        const days = moment().startOf('day').diff(moment(row.prevCompletionDate), 'days')
        if (days < 14) {
          const r = await dialogs.showConfirmDialog('実施', `前回の完了（${moment(row.prevCompletionDate).format('M/D')}）から ${days} 日しか経っていません。よろしいですか？`, 'はい', 'キャンセル')
          if (r !== 'YES') {
            return
          }
        }
      }
      this.$router.push({ name: 'QualityReportForm', query: { id: String(row.id) } })
    },

    /**
     * その物件のマスタ（撮影設定）を別タブで開く。この画面の月・検索条件を残すため同じタブでは移動しない
     */
    openMaster(row) {
      const r = this.$router.resolve({ name: 'QualityReportSiteSetting', query: { siteCode: row.siteCode } })
      window.open(r.href, '_blank')
    },

    /**
     * PDF を別タブで開く
     * API: qualityReport/pdf
     */
    async openPdf(row) {
      this.making = true
      const tab = window.open('', '_blank')
      if (tab) {
        tab.document.write('<p style="font-family:sans-serif;padding:20px;">PDF作成中…</p>')
      }
      try {
        const res = await backend.postBlob2('qualityReport/pdf', { id: row.id })
        const url = (window.URL || window.webkitURL).createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        if (tab) {
          tab.location.href = url
        } else {
          window.open(url, '_blank')
        }
      } catch (e) {
        console.log(e)
        if (tab) {
          tab.close()
        }
        await dialogs.showErrorDialog('PDF', 'PDF を作れませんでした。もう一度お試しください。')
      }
      this.making = false
    }
  }
}
</script>

<style scoped>
.filter-input {
  height: 38px;
  box-sizing: border-box;
}

/* 未完了・完了のチェック（@tailwindcss/forms なので色は color で） */
.checks input[type='checkbox'] {
  color: #16a34a;
  cursor: pointer;
}

/* 物件名の横の「マスタ」リンク（その物件の撮影設定を別タブで開く） */
.master-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 8px;
  font-size: 11px;
  font-weight: normal;
  color: #2563eb;
  cursor: pointer;
  white-space: nowrap;
}

.master-link:hover {
  text-decoration: underline;
}

/* 完了した行・カードは押して報告書を開く。乗せたときの水色は定期清掃の完了一覧と同じ（Tailwind の bg-gray-50 が !important なので、こちらも !important） */
.done-row {
  cursor: pointer;
}

.done-row:hover {
  background: rgba(163, 231, 228, 0.67) !important;
}

/* 「地図で見る」（地図を出しているときだけ。押しやすい高さ） */
.pin-link {
  font-size: 12px;
  min-height: 24px;
}

/* 進捗の表の「マスタ」列（列の中なので左の余白なし・少し大きく） */
.master-link-col {
  margin-left: 0;
  font-size: 13px;
}

/* 未完了・完了のチェックはタブの真下＝画面の左右中央（PC）。3 列の grid で真ん中にチェック、右に月と検索欄。
   右の検索欄が広くて中央に置けない幅では、重ならないようチェックが左へ寄る。狭い画面は流れのまま */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

@media screen and (min-width: 821px) {
  .filters {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
  }

  .filters .filters-center {
    grid-column: 2;
    flex-wrap: nowrap;
  }

  .filters .filters-right {
    grid-column: 3;
  }
}

/* タブは画面の左右中央（PC）。ヘッダーの行は左に余白（sm:pl-4／lg:pl-8）があるので、その半分だけ左へずらして画面の中央に合わせる。
   狭い画面は題名と重なるので流れのまま */
@media screen and (min-width: 821px) {
  .tabs {
    position: absolute;
    left: calc(50% - 0.5rem);
    transform: translateX(-50%);
    margin-left: 0;
  }
}

@media screen and (min-width: 1024px) {
  .tabs {
    left: calc(50% - 1rem);
  }
}

.chip {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 1px 9px;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
}

.chip-none {
  background: #f3f4f6;
  color: #6b7280;
}

.chip-plan {
  background: #eff6ff;
  color: #1d4ed8;
}

.chip-doing {
  background: #fffbeb;
  color: #b45309;
}

.chip-done {
  background: #d1fae5;
  color: #047857;
}

/* MAP の切り替え（白枠。出している間は黄色） */
/* 絞り込みが入っている欄は黄色（RlsForm と同じ） */
.has-value {
  background-color: yellow;
  font-weight: bold;
}

/* 地図の切り替え（丸いピル。OFF＝白地に青、ON＝青地に白。黄色は検索欄の印に使うので使わない） */
.map-btn {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 16px 0 12px;
  border: 2px solid #fff;
  border-radius: 9999px;
  background: #fff;
  color: #2563eb;
  font-weight: bold;
  font-size: 14px;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.map-btn-active {
  background: #2563eb;
  color: #fff;
}

/* 地図は表の上。凡例はその下に 1 行 */
.qr-map {
  height: 460px;
}

.qr-map-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  padding: 6px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 12px;
  font-weight: bold;
  color: #374151;
}

.qr-map-legend .dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  margin-right: 4px;
}

/* 現在地（青い丸。実施画面と同じ） */
.qr-map-legend .dot-me {
  background: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

/* ピンを押したときのダイアログ */
.pin-dialog-bg {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.pin-dialog {
  background: #fff;
  border-radius: 10px;
  padding: 18px 20px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

/* 「実施する」は予定日が無いと押せない＝グレー（共通ボタンの無効色は indigo なので上書き） */
.start-btn:disabled {
  background-color: #9ca3af !important;
  box-shadow: none;
}

/* マスタ未設定（赤のバッジ） */
.chip-alert {
  background: #fee2e2;
  color: #b91c1c;
}

/* 振替（マスタの実施日と違う日） */
.chip-shift {
  background: #fef3c7;
  color: #92400e;
}

.list-table td,
.sub-table td {
  vertical-align: middle;
}

/* 表の列は折り返さない（物件名・住所・得意先も 1 行。はみ出す分は表の枠の中で横スクロール） */
.list-table th,
.list-table td,
.staff-table th,
.staff-table td,
.sub-table th,
.sub-table td {
  white-space: nowrap;
}

/* スタッフごとの「進み」の棒 */
.bar {
  flex: 1 1 0;
  height: 10px;
  background: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
}

.bar-in {
  height: 100%;
  background: #16a34a;
  border-radius: 9999px;
}

/* スマホのカード（PC では出さない） */
.cards {
  display: none;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
}

.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
}

.card-btns {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.card-btn {
  min-height: 44px;
  flex: 1 1 0;
  max-width: 180px;
  justify-content: center;
}

@media screen and (max-width: 820px) {
  /* 進捗の表はカードに替える。スタッフごとの表はそのまま（横スクロール） */
  .list-table {
    display: none;
  }

  .cards {
    display: flex;
  }

  /* 検索欄は横いっぱいに分ける。入力の文字は 16px（iOS で拡大されない） */
  .filters .filter-input {
    flex: 1 1 140px;
    min-width: 0;
    width: auto;
    font-size: 16px;
    height: 44px;
  }

  .search-btn {
    width: 5.5rem;
    min-height: 44px;
  }

  /* 検索ボタンと同じ働きなので、狭い画面では出さない */
  .refresh-icon {
    display: none;
  }
}

/* タブレット（600〜820px）はカードを 2 列 */
@media screen and (min-width: 600px) and (max-width: 820px) {
  .cards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* 検索欄が 2 段になってもヘッダーに被らないよう、ヘッダーの下に流して画面ごとスクロール */
  main {
    position: static;
    height: auto;
    min-height: 60vh;
    margin-top: 4px;
  }
}

/* スマホ・タブレット（ルーターで PC と同じ見た目＝幅 980px 相当で縮めて出す）。PC の幅より狭く入りきらないので、
   タブは中央固定でなく流れに置き、ヘッダーの 1 行目は折り返し、月・チェック・検索欄は流れのまま、
   一覧は固定位置でなくヘッダーの下に流して画面ごとスクロール（検索欄が 2 段になっても被らない） */
.is-touch >>> nav .h-16 {
  height: auto !important;
  flex-wrap: wrap;
  padding: 4px 0;
}

.is-touch .tabs {
  position: static;
  transform: none;
  margin-left: 1rem;
}

.is-touch .filters {
  display: flex;
}

.is-touch .filters .filters-center {
  flex-wrap: wrap;
}

.is-touch main {
  position: static;
  height: auto;
  min-height: 60vh;
  margin-top: 4px;
}

/* ページ送り（AKBS 登録済み一覧と同じ見た目） */
#pagenation >>> ul {
  display: flex !important;
  color: rgb(6, 125, 194);
  font-weight: bold !important;
  cursor: pointer;
}

#pagenation >>> ul > li {
  padding: 0 10px;
}

#pagenation >>> ul > .active {
  background: rgb(6, 125, 194);
  color: white;
}

#pagenation >>> p {
  color: gray;
  font-size: small;
}
</style>
